import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import PaginationComponent from "../../../Components/PaginationComponent";
import toast from "react-hot-toast";
import attendanceService from "../../../Services/attendanceService";
import doctorService from "../../../Services/doctorService";
import staffService from "../../../Services/staffService";

const STATUS_OPTIONS = ["Present", "Late", "Absent"];

const statusStyles = {
  Present: "bg-green-50 text-green-600",
  Late: "bg-yellow-50 text-yellow-600",
  Absent: "bg-red-50 text-red-600",
};

const todayStr = () => new Date().toISOString().split("T")[0];

const Attendance = () => {
  const [roleFilter, setRoleFilter] = useState("All");
  const [selectedDate, setSelectedDate] = useState(todayStr());
  const [searchData, setSearchData] = useState("");
  const [rawAttendance, setRawAttendance] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [paginatedData, setPaginatedData] = useState([]);
  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [peopleList, setPeopleList] = useState([]);
  const [markRole, setMarkRole] = useState("Doctor");
  const [personSearch, setPersonSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [markStatus, setMarkStatus] = useState("Present");
  const [checkInTime, setCheckInTime] = useState("");
  const [checkOutTime, setCheckOutTime] = useState("");
  const [marking, setMarking] = useState(false);

  const dropdownRef = useRef(null);
  const [editRecord, setEditRecord] = useState(null);
  const [editStatus, setEditStatus] = useState("Present");
  const [editCheckIn, setEditCheckIn] = useState("");
  const [editCheckOut, setEditCheckOut] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  useEffect(() => {
    const getPeople = async () => {
      try {
        const [doctorsRes, staffRes] = await Promise.all([
          doctorService.fetchAllDoctors(),
          staffService.fetchAll(),
        ]);

        const doctors = (doctorsRes?.doctors || doctorsRes?.data || []).map(
          (d) => ({
            person_id: d.id,
            name: d.name,
            role: "Doctor",
          })
        );
        const staff = (
          staffRes?.staffs ||
          staffRes?.staff ||
          staffRes?.data ||
          []
        ).map((s) => ({
          person_id: s.staff_id ?? s.id,
          name: s.full_name ?? s.name,
          role: "Staff",
        }));

        setPeopleList([...doctors, ...staff]);
      } catch {
        toast.error("Error fetching doctors/staff list");
      }
    };

    getPeople();
  }, []);
  const getAttendance = async () => {
    try {
      setLoading(true);
      const response = await attendanceService.fetchAll({
        role: roleFilter !== "All" ? roleFilter : undefined,
        date: selectedDate || undefined,
      });
      setRawAttendance(response?.attendance || []);
      setLoading(false);
    } catch {
      setLoading(false);
      toast.error("Error fetching attendance");
    }
  };

  useEffect(() => {
    getAttendance();  }, [roleFilter, selectedDate]);
  useEffect(() => {
    const filteredResult = (rawAttendance || []).filter((item) =>
      item?.name?.toLowerCase().includes(searchData.toLowerCase())
    );
    setFilteredData(filteredResult);
    setCurrentPage(1);
  }, [searchData, rawAttendance]);
  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedData(filteredData?.slice(startIndex, endIndex) || []);
  }, [currentPage, filteredData]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString();
  };

  const formatTime = (t) => (t ? t : "-");
  const filteredPeople = peopleList.filter((p) => {
    const matchesRole = p.role === markRole;
    const matchesSearch = p.name
      ?.toLowerCase()
      .includes(personSearch.toLowerCase());
    return matchesRole && matchesSearch;
  });

  const handleSelectPerson = (person) => {
    setSelectedPerson(person);
    setPersonSearch(person.name);
    setShowDropdown(false);
  };

  const resetMarkForm = () => {
    setSelectedPerson(null);
    setPersonSearch("");
    setMarkStatus("Present");
    setCheckInTime("");
    setCheckOutTime("");
  };

  const handleMarkRoleChange = (role) => {
    setMarkRole(role);
    resetMarkForm();
  };

  const handleMarkAttendance = async () => {
    if (!selectedPerson) {
      toast.error("Please select a person first");
      return;
    }
    if (!selectedDate) {
      toast.error("Please select a date");
      return;
    }

    try {
      setMarking(true);
      await attendanceService.mark({
        person_id: selectedPerson.person_id,
        role: selectedPerson.role,
        date: selectedDate,
        status: markStatus,
        check_in_time: checkInTime || null,
        check_out_time: checkOutTime || null,
      });
      toast.success(`Attendance marked for ${selectedPerson.name}`);
      resetMarkForm();
      getAttendance();
    } catch {
      toast.error("Error marking attendance");
    } finally {
      setMarking(false);
    }
  };

  const handleQuickStatusUpdate = async (record, newStatus) => {
    try {
      await attendanceService.updateStatus({
        id: record.id,
        status: newStatus,
      });
      toast.success("Status updated");
      getAttendance();
    } catch {
      toast.error("Error updating status");
    }
  };
  const openEditModal = (record) => {
    setEditRecord(record);
    setEditStatus(record.status || "Present");
    setEditCheckIn(record.check_in_time || "");
    setEditCheckOut(record.check_out_time || "");
  };

  const closeEditModal = () => {
    setEditRecord(null);
    setEditStatus("Present");
    setEditCheckIn("");
    setEditCheckOut("");
  };

  const handleSaveEdit = async () => {
    if (!editRecord) return;

    try {
      setSaving(true);
      await attendanceService.mark({
        person_id: editRecord.person_id,
        role: editRecord.role,
        date: editRecord.date,
        status: editStatus,
        check_in_time: editCheckIn || null,
        check_out_time: editCheckOut || null,
      });
      toast.success("Attendance updated");
      closeEditModal();
      getAttendance();
    } catch {
      toast.error("Error updating attendance");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-3 sm:p-6">
      <div className="bg-white border border-gray-200 rounded-xl px-4 sm:px-6 py-4 mb-4 sm:mb-5 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-[15px] sm:text-[17px] font-[500] text-gray-800">
              Attendance Record
            </h1>
            <p className="text-[11px] sm:text-[12px] text-gray-400 mt-0.5">
              {filteredData?.length || 0} records found
            </p>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full sm:w-auto text-[13px] bg-gray-50 border border-gray-200 rounded-lg px-3 py-[7px] text-gray-600"
            >
              <option value="All">All Roles</option>
              <option value="Doctor">Doctor</option>
              <option value="Staff">Staff</option>
            </select>

            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full sm:w-auto text-[13px] bg-gray-50 border border-gray-200 rounded-lg px-3 py-[7px]"
            />

            <div className="relative w-full sm:w-[200px]">
              <SearchIcon
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                style={{ fontSize: 16 }}
              />
              <input
                type="search"
                placeholder="Search name..."
                value={searchData}
                onChange={(e) => setSearchData(e.target.value)}
                className="pl-8 pr-3 py-[7px] w-full text-[13px] bg-gray-50 border border-gray-200 rounded-lg"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl px-4 sm:px-6 py-4 mb-4 sm:mb-5 shadow-sm">
        <h2 className="text-[13px] font-[500] text-gray-700 mb-3 flex items-center gap-1.5">
          <PersonAddAlt1Icon style={{ fontSize: 17 }} className="text-[#2F6DB4]" />
          Mark Attendance
        </h2>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2.5">
          <select
            value={markRole}
            onChange={(e) => handleMarkRoleChange(e.target.value)}
            className="w-full sm:w-auto text-[13px] bg-gray-50 border border-gray-200 rounded-lg px-3 py-[7px] text-gray-600"
          >
            <option value="Doctor">Doctor</option>
            <option value="Staff">Staff</option>
          </select>

          <div className="relative w-full sm:w-[240px]" ref={dropdownRef}>
            <SearchIcon
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
              style={{ fontSize: 16 }}
            />
            <input
              type="text"
              placeholder={`Select ${markRole}...`}
              value={personSearch}
              onFocus={() => setShowDropdown(true)}
              onChange={(e) => {
                setPersonSearch(e.target.value);
                setSelectedPerson(null);
                setShowDropdown(true);
              }}
              className="pl-8 pr-8 py-[7px] w-full text-[13px] bg-gray-50 border border-gray-200 rounded-lg"
            />
            {personSearch && (
              <button
                type="button"
                onClick={resetMarkForm}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <CloseIcon style={{ fontSize: 15 }} />
              </button>
            )}

            {showDropdown && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-md max-h-56 overflow-y-auto">
                {filteredPeople.length === 0 ? (
                  <div className="px-3 py-3 text-[12px] text-gray-400 text-center">
                    No {markRole.toLowerCase()} found
                  </div>
                ) : (
                  filteredPeople.map((p) => (
                    <div
                      key={`${p.role}-${p.person_id}`}
                      onClick={() => handleSelectPerson(p)}
                      className="px-3 py-2 text-[13px] hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                    >
                      <span className="text-gray-800">{p.name}</span>
                      <span className="text-[10px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                        {p.role}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-1.5">
            {STATUS_OPTIONS.map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => setMarkStatus(status)}
                className={`text-[12px] px-3 py-[7px] rounded-lg border transition-colors ${
                  markStatus === status
                    ? statusStyles[status] + " border-transparent font-[600]"
                    : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"
                }`}
              >
                {status}
              </button>
            ))}
          </div>

          <input
            type="time"
            value={checkInTime}
            onChange={(e) => setCheckInTime(e.target.value)}
            className="text-[13px] bg-gray-50 border border-gray-200 rounded-lg px-3 py-[7px] w-full sm:w-auto"
          />
          <input
            type="time"
            value={checkOutTime}
            onChange={(e) => setCheckOutTime(e.target.value)}
            className="text-[13px] bg-gray-50 border border-gray-200 rounded-lg px-3 py-[7px] w-full sm:w-auto"
          />

          <button
            type="button"
            onClick={handleMarkAttendance}
            disabled={marking || !selectedPerson}
            className="text-[13px] bg-[#2F6DB4] hover:bg-[#255a95] disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-[7px] rounded-lg w-full sm:w-auto"
          >
            {marking ? "Marking..." : "Mark"}
          </button>
        </div>
      </div>
      <div className="hidden sm:block bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[780px]">
            <thead>
              <tr className="bg-[#2F6DB4] text-[11px] uppercase text-white">
                <th className="py-3 px-4 text-left">Name</th>
                <th className="py-3 px-4 text-center">Role</th>
                <th className="py-3 px-4 text-center">Date</th>
                <th className="py-3 px-4 text-center">Check-in</th>
                <th className="py-3 px-4 text-center">Check-out</th>
                <th className="py-3 px-4 text-center">Status</th>
                <th className="py-3 px-4 text-center">Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-gray-400 text-[13px]">
                    Loading...
                  </td>
                </tr>
              ) : paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-gray-400 text-[13px]">
                    No attendance marked yet
                  </td>
                </tr>
              ) : (
                paginatedData.map((record) => (
                  <tr key={record.id} className="border-t hover:bg-gray-50 text-[13px]">
                    <td className="py-3 px-4 font-[500] text-gray-800">
                      {record.name}
                    </td>
                    <td className="py-3 px-4 text-center text-gray-600">
                      {record.role}
                    </td>
                    <td className="py-3 px-4 text-center text-gray-600">
                      {formatDate(record.date)}
                    </td>
                    <td className="py-3 px-4 text-center text-gray-600">
                      {formatTime(record.check_in_time)}
                    </td>
                    <td className="py-3 px-4 text-center text-gray-600">
                      {formatTime(record.check_out_time)}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <select
                        value={record.status}
                        onChange={(e) =>
                          handleQuickStatusUpdate(record, e.target.value)
                        }
                        className={`text-[11px] font-[600] px-2.5 py-1 rounded-md border-0 cursor-pointer ${
                          statusStyles[record.status] || "bg-gray-50 text-gray-500"
                        }`}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => openEditModal(record)}
                        className="text-blue-600 hover:bg-blue-50 p-1.5 rounded-md"
                        title="Edit attendance"
                      >
                        <EditIcon style={{ fontSize: 16 }} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="sm:hidden space-y-2.5">
        {loading ? (
          <div className="text-center text-gray-400 text-[13px] py-10">
            Loading...
          </div>
        ) : paginatedData.length === 0 ? (
          <div className="text-center text-gray-400 text-[13px] py-10">
            No attendance marked yet
          </div>
        ) : (
          paginatedData.map((record) => (
            <div key={record.id} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <span className="text-[10px] font-bold text-[#2F6DB4]">
                    {record.role}
                  </span>
                  <p className="text-[14px] font-[600] text-gray-800">
                    {record.name}
                  </p>
                </div>

                <div className="flex items-center gap-1.5">
                  <select
                    value={record.status}
                    onChange={(e) =>
                      handleQuickStatusUpdate(record, e.target.value)
                    }
                    className={`text-[11px] font-[600] px-2.5 py-1 rounded-md border-0 cursor-pointer ${
                      statusStyles[record.status] || "bg-gray-50 text-gray-500"
                    }`}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>

                  <button
                    onClick={() => openEditModal(record)}
                    className="text-blue-600 hover:bg-blue-50 p-1.5 rounded-md"
                    title="Edit attendance"
                  >
                    <EditIcon style={{ fontSize: 16 }} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-[10px] text-gray-400">Date</p>
                  <p className="text-[13px] font-[600]">
                    {formatDate(record.date)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-[10px] text-gray-400">Check-in</p>
                  <p className="text-[13px] font-[600]">
                    {formatTime(record.check_in_time)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-[10px] text-gray-400">Check-out</p>
                  <p className="text-[13px] font-[600]">
                    {formatTime(record.check_out_time)}
                  </p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <PaginationComponent
        filteredData={filteredData}
        setPaginatedData={setPaginatedData}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />
      {editRecord &&
        createPortal(
          <div className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-lg w-full max-w-sm">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <div>
                  <h3 className="text-[14px] font-[600] text-gray-800">
                    Edit Attendance
                  </h3>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    {editRecord.name} · {formatDate(editRecord.date)}
                  </p>
                </div>
                <button
                  onClick={closeEditModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <CloseIcon style={{ fontSize: 18 }} />
                </button>
              </div>

              <div className="px-5 py-4 space-y-4">
                <div>
                  <label className="text-[11px] text-gray-500 mb-1.5 block">
                    Status
                  </label>
                  <div className="flex items-center gap-1.5">
                    {STATUS_OPTIONS.map((status) => (
                      <button
                        key={status}
                        type="button"
                        onClick={() => setEditStatus(status)}
                        className={`text-[12px] px-3 py-[7px] rounded-lg border transition-colors flex-1 ${
                          editStatus === status
                            ? statusStyles[status] + " border-transparent font-[600]"
                            : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"
                        }`}
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] text-gray-500 mb-1.5 block">
                      Check-in
                    </label>
                    <input
                      type="time"
                      value={editCheckIn}
                      onChange={(e) => setEditCheckIn(e.target.value)}
                      className="text-[13px] bg-gray-50 border border-gray-200 rounded-lg px-3 py-[7px] w-full"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] text-gray-500 mb-1.5 block">
                      Check-out
                    </label>
                    <input
                      type="time"
                      value={editCheckOut}
                      onChange={(e) => setEditCheckOut(e.target.value)}
                      className="text-[13px] bg-gray-50 border border-gray-200 rounded-lg px-3 py-[7px] w-full"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
                <button
                  onClick={closeEditModal}
                  className="text-[13px] px-4 py-[7px] rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={saving}
                  className="text-[13px] bg-[#2F6DB4] hover:bg-[#255a95] disabled:opacity-50 text-white px-4 py-[7px] rounded-lg"
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default Attendance;